import { useFormikContext } from 'formik'
import { Form } from 'components/Wizards/Form/Form'
import { Button } from 'blaze-ui/Button'
import { Values } from 'components/Wizards/schemas/TurnInto'
import { ArrowRight } from 'blaze-ui/icons'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Sections } from 'components/Wizards/Wizard/Form/Sections'
import { useEffect } from 'react'
import { isEqual, partition, shuffle, uniqWith } from 'lodash'
import { FormStatus } from 'components/Wizards/Wizard/Form/Types'
import { fetchVisualElements } from 'hooks/useVisualTemplates'
import { useNativeLayouts } from 'components/Wizards/hooks/useNativeLayouts'
import { DesignChannel } from 'components/NewFileModal/Types'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import {
  ConditionalLanguageInputField as LanguageInputField,
  ConditionalInlineLanguageInputField as InlineLanguageInputField,
} from '../../Wizard/Form/LanguageInputField/ConditionalLanguageInputField'

export const ConfirmDetails = ({
  goBack,
  isMultiSelect,
  isLoading,
  initialIsCampaign,
}: {
  goBack: () => void
  isMultiSelect: boolean
  initialIsCampaign?: boolean
  isLoading: boolean
}) => {
  const { isMobile } = useResponsiveDesign()
  const formikProps = useFormikContext<Values>()

  const { values, status } = formikProps
  const isAutofilling = status === FormStatus.AutoFilling
  const channels = values.channels
  const [designChannels, otherChannels] = partition(channels, (channel) => !!channel.width && !!channel.height)
  const topic = values.focus ?? values.topic

  const nativeLayouts = useNativeLayouts(values.brand_kit_id)

  const isGeneratingDesigns = designChannels.some((c) => !c.content)
  const isButtonDisabled =
    !formikProps.values.filename ||
    (isMultiSelect && !formikProps.values.channels?.length) ||
    isLoading ||
    isGeneratingDesigns

  const findTemplates = async (aspectRatios: { width: number; height: number }[]) => {
    const seed = Math.random().toString(36).substring(2)
    return await Promise.all(
      aspectRatios.map(async ({ width, height }) => {
        const response = await fetchVisualElements({
          width,
          height,
          items: 1,
          basic_layout: true,
          order_by: 'random',
          has_replaceable: true,
          replaceable_has_background: true,
          additional_seed: seed,
        })

        return response.data.visual_elements[0]
      }),
    )
  }

  useEffect(() => {
    const assignTemplatesToChannels = async () => {
      if (!topic || designChannels.length === 0) return

      const aspectRatios = uniqWith(
        designChannels.map((c) => ({ width: c.width ?? 0, height: c.height ?? 0 })),
        isEqual,
      )

      const templates = await findTemplates(aspectRatios)

      formikProps.setFieldValue('channels', [
        ...designChannels.map((channel) => {
          const nativeLayout = nativeLayouts[channel.channel_type as DesignChannel]
          if (nativeLayout?.enabled) {
            const { archetypes, width, height } = nativeLayout
            return {
              ...channel,
              isNativeLayout: true,
              archetype: shuffle(archetypes)[0],
              width,
              height,
            }
          }

          const sourceVisualElementId = templates.find(
            (t) => t && t.width === channel.width && t.height === channel.height,
          )?.id
          return {
            ...channel,
            source_visual_element_id: sourceVisualElementId,
          }
        }),
        ...otherChannels,
      ])
    }

    if (!isAutofilling) {
      assignTemplatesToChannels()
    }
  }, [isAutofilling, topic]) // eslint-disable-line react-hooks/exhaustive-deps

  const submitLabel = isGeneratingDesigns ? 'Generating...' : `Generate ${initialIsCampaign ? 'Campaign' : 'Content'}`

  return (
    <Form.Root>
      <Sections.AutoFillFieldsSection isHeaderVisible={false}>
        <ResponsiveDesign.Mobile>
          <LanguageInputField />
        </ResponsiveDesign.Mobile>
      </Sections.AutoFillFieldsSection>
      <Form.Actions isSticky>
        <Form.PreviousButton onPress={goBack}>Previous Step</Form.PreviousButton>

        <Form.PrimaryActions>
          <ResponsiveDesign.Desktop>
            <InlineLanguageInputField />
          </ResponsiveDesign.Desktop>
          <Button
            size="lg"
            onPress={() => formikProps.submitForm()}
            endIcon={!isGeneratingDesigns ? ArrowRight : undefined}
            fullWidth={isMobile}
            isDisabled={isButtonDisabled}
            aria-label={submitLabel}
          >
            {submitLabel}
          </Button>
        </Form.PrimaryActions>
      </Form.Actions>
    </Form.Root>
  )
}
